/**
 * The ware catalogue (spec §4.1; T32 since 5(e): the new raw ore at index 6, the new alloy appended). The ids live here as a const tuple so they are types; the data
 * (`src/data/strategic/wares.json`) must list exactly these ids in this order (checked by the schema).
 * The ware index is the position in `WARE_IDS`, and every per-ware typed array is laid out in that order.
 */

export const WARE_IDS = [
  'ferrite',
  'silicates',
  'ice',
  'titanium',
  'platinum',
  'tetherstone',
  'refractory_ore',
  'steel',
  'ti_alloy',
  'silicon_wafers',
  'water',
  'fuel_cells',
  'hull_plating',
  'electronics',
  'drive_parts',
  'weapon_parts',
  'food',
  'medical',
  'refractory_alloy',
] as const
export type WareId = (typeof WARE_IDS)[number]
export const WARE_COUNT = WARE_IDS.length

/** M2's raw wares, in M2's order (seam S-M2-1). They are the first ware indices. */
export const RAW_WARE_IDS = ['ferrite', 'silicates', 'ice', 'titanium', 'platinum', 'tetherstone', 'refractory_ore'] as const
export type RawWareId = (typeof RAW_WARE_IDS)[number]
export const RAW_WARE_COUNT = RAW_WARE_IDS.length

/** The component-tier wares: the only wares hull build costs may name (spec §4.6). */
export const COMPONENT_WARE_IDS = ['hull_plating', 'electronics', 'drive_parts', 'weapon_parts'] as const
export type ComponentWareId = (typeof COMPONENT_WARE_IDS)[number]

/** M2's salvage component types (M2 `COMPONENT_TYPES`). They are not wares; a sold one deposits a ware yield (spec §5.7). */
export const COMPONENT_TYPES = ['reactor', 'radiator', 'weapon', 'shieldGen', 'thruster', 'cargoPod', 'dataCore'] as const
export type ComponentType = (typeof COMPONENT_TYPES)[number]

export const WARE_TIERS = ['raw', 'refined', 'component', 'end'] as const
export type WareTier = (typeof WARE_TIERS)[number]

const INDEX: ReadonlyMap<string, number> = new Map(WARE_IDS.map((id, i) => [id, i]))

/** The ware index of `id` (its position in `WARE_IDS`). */
export function wareIndex(id: WareId): number {
  return INDEX.get(id) as number
}

/** The ware index of an untrusted string, or -1 when it is not a ware id. */
export function wareIndexOf(id: string): number {
  return INDEX.get(id) ?? -1
}

export function isWareId(id: string): id is WareId {
  return INDEX.has(id)
}

/** The ware id at index `w`. Throws on an out-of-range index. */
export function wareIdAt(w: number): WareId {
  const id = WARE_IDS[w]
  if (id === undefined) throw new Error(`wareIdAt: no ware at index ${w}`)
  return id
}

export function isComponentType(id: string): id is ComponentType {
  return (COMPONENT_TYPES as readonly string[]).includes(id)
}

// ---------------------------------------------------------------------------------------------
// The v6 save layout and its remap onto the live catalogue (MC-e1, T32; called by MC-e5a1–a3)
// ---------------------------------------------------------------------------------------------

/**
 * The raw wares of the strategic save v6 layout, frozen: the catalogue before 5(e). Never edit these lists. A v6
 * save's per-ware columns, ware indices and ware bitmasks are laid out by them, whatever the live catalogue holds.
 */
export const V6_RAW_WARE_IDS = ['ferrite', 'silicates', 'ice', 'titanium', 'platinum', 'tetherstone'] as const satisfies readonly WareId[]
/** Every ware of the v6 layout, frozen, raw wares first. */
export const V6_WARE_IDS = [
  ...V6_RAW_WARE_IDS,
  'steel',
  'ti_alloy',
  'silicon_wafers',
  'water',
  'fuel_cells',
  'hull_plating',
  'electronics',
  'drive_parts',
  'weapon_parts',
  'food',
  'medical',
] as const satisfies readonly WareId[]
export const V6_WARE_COUNT = V6_WARE_IDS.length
export const V6_RAW_WARE_COUNT = V6_RAW_WARE_IDS.length

/** The most wares a layout may hold: a ware bitmask must stay a non-negative int32 (`1 << 30` is the top bit). */
const MASK_WARES_MAX = 30

/**
 * A remap of an old ware layout onto a new one, matched by ware id (T32 inserts the new raw ore at the end of the
 * raw block and appends the new alloy, so every old ware keeps its order). Built once at load; read-only.
 */
export interface WareRemap {
  readonly oldCount: number
  readonly oldRawCount: number
  readonly newCount: number
  readonly newRawCount: number
  /** The new index of old ware index `w`. */
  readonly toNew: Int32Array
  /** The old index of new ware index `w`; −1 for a ware the old layout lacks. */
  readonly toOld: Int32Array
  /** The new indices the old layout lacks, ascending (T32: 6 and 18). */
  readonly added: readonly number[]
  /** Every ware of each layout as a bitmask (`(1 << count) − 1`), and every raw ware. */
  readonly oldAllMask: number
  readonly newAllMask: number
  readonly oldAllRawMask: number
  readonly newAllRawMask: number
}

/**
 * Builds the remap from layout `oldIds` (its first `oldRawCount` ids raw) onto `newIds` (first `newRawCount` raw).
 * Throws when an old ware is missing from the new layout, an id repeats, a raw ware leaves the raw block (or a
 * non-raw ware enters it), or a layout has more wares than a bitmask holds.
 */
export function buildWareRemap(oldIds: readonly string[], oldRawCount: number, newIds: readonly string[], newRawCount: number): WareRemap {
  if (oldIds.length > MASK_WARES_MAX || newIds.length > MASK_WARES_MAX) throw new Error(`buildWareRemap: more than ${MASK_WARES_MAX} wares`)
  if (oldRawCount > oldIds.length || newRawCount > newIds.length) throw new Error('buildWareRemap: raw count exceeds the layout')
  const newIndex = new Map<string, number>()
  newIds.forEach((id, i) => {
    if (newIndex.has(id)) throw new Error(`buildWareRemap: "${id}" repeats in the new layout`)
    newIndex.set(id, i)
  })
  const toNew = new Int32Array(oldIds.length)
  const toOld = new Int32Array(newIds.length).fill(-1)
  oldIds.forEach((id, w) => {
    const n = newIndex.get(id)
    if (n === undefined) throw new Error(`buildWareRemap: "${id}" is missing from the new layout`)
    if (toOld[n] !== -1) throw new Error(`buildWareRemap: "${id}" repeats in the old layout`)
    if (w < oldRawCount !== n < newRawCount) throw new Error(`buildWareRemap: "${id}" changes tier (raw block)`)
    toNew[w] = n
    toOld[n] = w
  })
  const added: number[] = []
  toOld.forEach((w, n) => {
    if (w === -1) added.push(n)
  })
  return {
    oldCount: oldIds.length,
    oldRawCount,
    newCount: newIds.length,
    newRawCount,
    toNew,
    toOld,
    added,
    oldAllMask: (1 << oldIds.length) - 1,
    newAllMask: (1 << newIds.length) - 1,
    oldAllRawMask: (1 << oldRawCount) - 1,
    newAllRawMask: (1 << newRawCount) - 1,
  }
}

/** The v6 save layout onto the live catalogue. The identity until MC-e2 grows `WARE_IDS`; T32 after it. */
export const V6_REMAP: WareRemap = buildWareRemap(V6_WARE_IDS, V6_RAW_WARE_COUNT, WARE_IDS, RAW_WARE_COUNT)

/**
 * The new index of an old saved ware index (agent cargo, event ring, line `missing`, supply priority). A negative
 * value is the "none" sentinel (−1) and passes through. Throws on a non-integer or an index past the old layout.
 */
export function remapWareIndex(oldIndex: number, remap: WareRemap = V6_REMAP): number {
  if (!Number.isInteger(oldIndex) || oldIndex >= remap.oldCount) throw new Error(`remapWareIndex: no old ware at index ${oldIndex}`)
  return oldIndex < 0 ? oldIndex : remap.toNew[oldIndex]
}

/** The old index of new ware index `newIndex`, −1 for a ware the old layout lacks. Throws past the new layout. */
export function oldWareIndex(newIndex: number, remap: WareRemap = V6_REMAP): number {
  if (!Number.isInteger(newIndex) || newIndex < 0 || newIndex >= remap.newCount) throw new Error(`oldWareIndex: no ware at index ${newIndex}`)
  return remap.toOld[newIndex]
}

/**
 * Re-strides an old ware bitmask (fleet `wareAllow`/`wareBlock`). Every old ware (the "any ware" default) becomes
 * every new ware; any other mask keeps exactly its wares at their new bits, so a partial mask gains no added ware.
 * Throws on a value that isn't a mask of the old layout.
 */
export function restrideWareMask(oldMask: number, remap: WareRemap = V6_REMAP): number {
  if (!Number.isInteger(oldMask) || oldMask < 0 || oldMask > remap.oldAllMask) throw new Error(`restrideWareMask: ${oldMask} is not an old ware mask`)
  if (oldMask === remap.oldAllMask) return remap.newAllMask
  let out = 0
  for (let w = 0; w < remap.oldCount; w++) if ((oldMask & (1 << w)) !== 0) out |= 1 << remap.toNew[w]
  return out
}

/**
 * Re-strides a mine order's ore mask (raw indices, which are the first ware indices). Every old ore becomes every
 * new ore; anything else follows `restrideWareMask`, so the "any ware" default stays every ware.
 */
export function restrideRawMask(oldMask: number, remap: WareRemap = V6_REMAP): number {
  return oldMask === remap.oldAllRawMask ? remap.newAllRawMask : restrideWareMask(oldMask, remap)
}

/** A writable number column (a typed array or a number array). */
export interface NumberColumn {
  [index: number]: number
}

/**
 * Copies one old per-ware row (`oldCount` values at `src[srcBase]`) into one new row (`newCount` values at
 * `dst[dstBase]`): each value moves to its ware's new index, and an added ware gets `fill`. `src` and `dst` must not
 * overlap. A station × ware column is re-strided by calling this once per station.
 */
export function restrideWareRow(src: ArrayLike<number>, srcBase: number, dst: NumberColumn, dstBase: number, fill = 0, remap: WareRemap = V6_REMAP): void {
  for (const n of remap.added) dst[dstBase + n] = fill
  for (let w = 0; w < remap.oldCount; w++) dst[dstBase + remap.toNew[w]] = src[srcBase + w]
}

/** `restrideWareRow` for a per-raw-ware row (field reserves: `oldRawCount` values in, `newRawCount` out). */
export function restrideRawRow(src: ArrayLike<number>, srcBase: number, dst: NumberColumn, dstBase: number, fill = 0, remap: WareRemap = V6_REMAP): void {
  for (const n of remap.added) if (n < remap.newRawCount) dst[dstBase + n] = fill
  for (let w = 0; w < remap.oldRawCount; w++) dst[dstBase + remap.toNew[w]] = src[srcBase + w]
}
